import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Sparkles, Loader2, ArrowRight, CheckCircle2, Award, FileText, Compass, Target,
} from "lucide-react";
import { toast } from "sonner";
import Markdown from "@/components/Markdown";
import { Skeleton } from "@/components/Skeleton";

export default function CareerCopilot() {
  const navigate = useNavigate();
  const [score, setScore] = useState(null);
  const [advice, setAdvice] = useState(null);
  const [goal, setGoal] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/career/score");
        setScore(data);
      } catch { toast.error("Could not load profile score"); }
    })();
  }, []);

  const requestAdvice = async () => {
    setBusy(true);
    setAdvice(null);
    try {
      const { data } = await api.post("/career/advice", { goal: goal || undefined });
      setAdvice(data);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail?.error === "INSUFFICIENT_COINS") {
        toast.error(`Need ${detail.required} coins · You have ${detail.balance}`, { action: { label: "Recharge", onClick: () => navigate("/wallet") } });
      } else {
        toast.error(typeof detail === "string" ? detail : "Could not generate advice");
      }
    } finally { setBusy(false); }
  };

  return (
    <Layout title="Career Copilot">
      {/* Header */}
      <div className="mb-6">
        <span className="tb-chip-sky inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold tracking-wide">
          <Compass className="h-3 w-3" /> Career Copilot
        </span>
        <h1 className="mt-3 font-display text-3xl sm:text-4xl tracking-tight font-medium leading-tight">
          Your AI career advisor
        </h1>
        <p className="mt-1.5 text-sm text-muted-foreground max-w-xl">
          Profile score, missing-section detection, mock interview prep and certification recommendations — tailored to your educator journey.
        </p>
      </div>

      {/* ===== Score ===== */}
      {!score ? (
        <Skeleton className="h-44 mb-5" />
      ) : (
        <div className="tb-card-feature relative overflow-hidden mb-6 grid grid-cols-1 md:grid-cols-12 gap-5" data-testid="career-score-card">
          <div className="absolute inset-0 tb-grid-bg opacity-50 pointer-events-none" />
          {/* Score gauge */}
          <div className="relative md:col-span-4 flex items-center gap-4">
            <ScoreRing value={score.score} />
            <div>
              <p className="text-[10px] uppercase tracking-[0.22em] font-semibold text-muted-foreground">Profile</p>
              <p className="font-display text-2xl tracking-tight font-medium">{score.profile_level}</p>
              <p className="text-xs text-muted-foreground">{score.score} / 100 complete</p>
            </div>
          </div>

          {/* Next actions */}
          <div className="relative md:col-span-8">
            <p className="text-[10px] uppercase tracking-[0.22em] font-semibold text-muted-foreground inline-flex items-center gap-2 mb-3">
              <Target className="h-3 w-3" /> Recommended next steps
            </p>
            {score.next_actions.length === 0 ? (
              <p className="text-sm text-muted-foreground">Your profile looks great. Generate your resume next.</p>
            ) : (
              <ul className="space-y-2">
                {score.next_actions.map((a) => (
                  <li key={a.id}>
                    <button
                      onClick={() => navigate(a.path || "/profile")}
                      className="w-full flex items-center gap-3 rounded-xl p-3 hover:bg-muted/60 transition text-left"
                      data-testid={`career-action-${a.id}`}
                    >
                      <span className="h-7 w-7 rounded-lg tb-chip-lavender grid place-items-center shrink-0">
                        <Sparkles className="h-3.5 w-3.5" />
                      </span>
                      <span className="flex-1 text-sm font-medium">{a.label}</span>
                      <span className="text-[10px] uppercase tracking-[0.18em] font-semibold text-muted-foreground">+{a.weight} pts</span>
                      <ArrowRight className="h-4 w-4 text-muted-foreground" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
        {/* Missing sections */}
        <div className="lg:col-span-5 tb-card p-5" data-testid="career-missing">
          <p className="text-[10px] uppercase tracking-[0.22em] font-semibold text-muted-foreground inline-flex items-center gap-2">
            <Award className="h-3 w-3" /> Profile completeness
          </p>
          <ul className="mt-3 space-y-1.5">
            {(score?.completed_fields || []).map((f) => (
              <li key={f.key} className="flex items-center gap-2 text-sm">
                <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                <span className="flex-1">{f.label}</span>
                <span className="text-[10px] text-muted-foreground">+{f.weight}</span>
              </li>
            ))}
            {(score?.missing_fields || []).map((f) => (
              <li key={f.key} className="flex items-center gap-2 text-sm opacity-60">
                <span className="h-4 w-4 rounded-full border border-dashed border-border" />
                <span className="flex-1">{f.label}</span>
                <span className="text-[10px] text-muted-foreground">+{f.weight}</span>
              </li>
            ))}
          </ul>
          <Button variant="outline" className="mt-4 w-full rounded-full" onClick={() => navigate("/profile")} data-testid="career-edit-profile">
            <Sparkles className="h-3.5 w-3.5 mr-1.5" /> Open profile editor
          </Button>
        </div>

        {/* Career advice */}
        <div className="lg:col-span-7 tb-card p-5" data-testid="career-advice">
          <p className="text-[10px] uppercase tracking-[0.22em] font-semibold text-muted-foreground inline-flex items-center gap-2">
            <Sparkles className="h-3 w-3" /> Get tailored advice
          </p>
          <p className="mt-2 text-sm text-muted-foreground">
            Tell TeeBee your goal — your next role, school type, geography — and get a structured action plan.
          </p>
          <Input
            value={goal}
            onChange={(e) => setGoal(e.target.value)}
            placeholder="e.g. Apply for IB curriculum lead position in Dubai"
            className="mt-3 h-11 rounded-xl"
            data-testid="career-goal-input"
          />
          <Button onClick={requestAdvice} disabled={busy} className="mt-3 w-full h-11 rounded-full" data-testid="career-generate">
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <Sparkles className="h-3.5 w-3.5 mr-1.5" />}
            Generate advice · 3 coins
          </Button>

          {advice && (
            <div className="mt-5 rounded-xl bg-muted/40 p-4 border border-border tb-fade-up">
              <Markdown>{advice.content}</Markdown>
            </div>
          )}

          <div className="mt-5 grid grid-cols-2 gap-2">
            <Button variant="outline" className="rounded-full" onClick={() => navigate("/resume")} data-testid="career-go-resume">
              <FileText className="h-3.5 w-3.5 mr-1.5" /> Build resume
            </Button>
            <Button variant="outline" className="rounded-full" onClick={() => navigate("/profile")}>
              Edit profile
            </Button>
          </div>
        </div>
      </div>
    </Layout>
  );
}

function ScoreRing({ value }) {
  const r = 38;
  const c = 2 * Math.PI * r;
  const offset = c - (Math.min(100, Math.max(0, value)) / 100) * c;
  return (
    <div className="relative" data-testid="career-score-ring">
      <svg width="96" height="96" viewBox="0 0 96 96" className="-rotate-90">
        <circle cx="48" cy="48" r={r} fill="none" stroke="hsl(var(--border))" strokeWidth="8" />
        <circle
          cx="48" cy="48" r={r} fill="none"
          stroke="hsl(var(--primary))" strokeWidth="8" strokeLinecap="round"
          strokeDasharray={c} strokeDashoffset={offset}
          style={{ transition: "stroke-dashoffset 0.6s ease-out" }}
        />
      </svg>
      <span className="absolute inset-0 grid place-items-center font-display text-2xl font-medium tracking-tight">
        {value}
      </span>
    </div>
  );
}
